import { faBalanceScaleLeft, faSearch } from "@fortawesome/pro-solid-svg-icons";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { BrandsStrollersList } from "@/mk2/apps/strollers/containers/BrandsStrollersList/BrandsStrollersList";
import { strollersFetchTrigger } from "@/mk2/apps/strollers/containers/BrandsStrollersList/BrandsStrollersList.actions";
import strollersReducer from "@/mk2/apps/strollers/containers/BrandsStrollersList/BrandsStrollersList.reducers";
import strollersSaga from "@/mk2/apps/strollers/containers/BrandsStrollersList/BrandsStrollersList.sagas";
import {
    getBrandStrollersListState,
    getBrandsStrollersListState,
} from "@/mk2/apps/strollers/containers/BrandsStrollersList/BrandsStrollersList.selectors";
import {
    strollersComparisonLoad,
    strollersComparisonToggle,
} from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.actions";
import strollersComparisonReducer from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.reducers";
import strollersComparisonSaga from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.sagas";
import { getStrollersComparisonState } from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.selectors";
import { LinkedObjects } from "@/mk2/apps/strollers/containers/StrollersDetail/StrollersDetail.actions";
import { PageMode } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.constants";
import { breadcrumbsBrand } from "@/mk2/apps/strollers/structuredData";
import {
    strollersComparisonUrl,
    strollersSearchUrl,
    strollersUrl,
} from "@/mk2/apps/strollers/urls";
import { Btn, BtnCountType, BtnLayout, BtnType } from "@/mk2/components/Btn";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, { HalfpageProps, PageRouteProps } from "@/mk2/pages/Page";
import { AppState, getRequestBaseUrl } from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    BlogPostEntity,
    BlogPostSchema,
    ContentItemEntity,
    ContentItemSchema,
    CounsellingEntity,
    CounsellingSchema,
    PhotoDetailSchema,
    StrollerBrandEntity,
    StrollerBrandSchema,
    StrollerEntity,
    StrollerPhoto,
    StrollerSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import {
    SEO_TITLE_STROLLERS_FROM_BRAND_brand,
    STROLLER_SEO_GENERAL_DESC,
} from "@/mk/autogenerated/translation";

interface OwnProps {}

interface StateProps {
    slug: string;
    strollers: StrollerEntity[];
    strollersPreviewPhotoMap: Record<number, number>;
    strollersPhotosMap: Record<number, StrollerPhoto>;
    hasMore: boolean;
    brand: StrollerBrandEntity;
    count: number;
    loadingState: LoadingState;
    errorMessage: string;
    comparison: number[];
    baseUrl: string;
    buyingGuide: ContentItemEntity;
    articles: BlogPostEntity[];
    counselling: CounsellingEntity;
    forumPostLinkedObjects: LinkedObjects;
}

interface DispatchProps {
    onLoad(slug: string, offset?: number);
    onStrollersComparisonLoad();
    onStrollersComparisonToggle(id: number, location: HistoryLocationWithState);
}

type RouteProps = PageRouteProps<{ slug: string }>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class BrandsStrollersListPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const slug = ownProps.routeParams.slug;

            const brandsStrollersListState = getBrandsStrollersListState(state);
            const brandStrollersListState = getBrandStrollersListState(state, slug);
            const strollersComparisonState = getStrollersComparisonState(state);

            const strollersPreviewPhotoMap = brandStrollersListState.strollersPreviewPhotoMap;

            const strollersPhotos = getDenormalizedEntities<StrollerPhoto>(
                state,
                PhotoDetailSchema,
                brandStrollersListState.strollersPhotosIds,
            );
            const strollersPhotosMap = {};
            strollersPhotos.map(
                (strollerPhoto) => (strollersPhotosMap[strollerPhoto.id] = strollerPhoto),
            );

            const brand = getDenormalizedEntity<StrollerBrandEntity>(
                state,
                StrollerBrandSchema,
                brandStrollersListState.brandId,
            );

            const articles = getDenormalizedEntities<BlogPostEntity>(
                state,
                BlogPostSchema,
                brandStrollersListState.articleIds,
            );

            return {
                articles,
                slug,
                errorMessage: brandStrollersListState.errorMessage,
                loadingState: brandStrollersListState.loadingState,
                strollers: getDenormalizedEntities<StrollerEntity>(
                    state,
                    StrollerSchema,
                    brandStrollersListState.strollerIds,
                ),
                strollersPreviewPhotoMap,
                strollersPhotosMap,
                hasMore: brandStrollersListState.hasMore,
                brand,
                count: brandStrollersListState.count,
                comparison: strollersComparisonState.allIds,
                baseUrl: getRequestBaseUrl(state),
                buyingGuide: getDenormalizedEntity<ContentItemEntity>(
                    state,
                    ContentItemSchema,
                    brandsStrollersListState.buyingGuideId,
                ),
                counselling: getDenormalizedEntity<CounsellingEntity>(
                    state,
                    CounsellingSchema,
                    brandsStrollersListState.counsellingId,
                ),
                forumPostLinkedObjects: brandsStrollersListState.forumPostLinkedObjects,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: strollersFetchTrigger,
            onStrollersComparisonToggle: strollersComparisonToggle,
            onStrollersComparisonLoad: strollersComparisonLoad,
        };
    }

    public getMetaTitle(props: Props): string {
        const { brand } = props;
        return brand
            ? interpolate(SEO_TITLE_STROLLERS_FROM_BRAND_brand, { brand: brand.name })
            : null;
    }

    public getMetaDescription(props: Props): string {
        const title = this.getMetaTitle(props);
        if (title) {
            return `${title} - ${STROLLER_SEO_GENERAL_DESC}`;
        }
    }

    public render(props: Props): false | JSX.Element {
        return <BrandsStrollersList {...props} />;
    }

    public getHeaderRight(props: Props): React.ReactElement {
        const { slug } = props;
        const query = { brand: slug };
        return (
            <>
                <Btn
                    type={BtnType.BlueText}
                    layout={BtnLayout.Icon}
                    icon={faBalanceScaleLeft}
                    count={props.comparison.length}
                    countType={BtnCountType.Circle}
                    link={strollersComparisonUrl}
                />
                <Btn
                    type={BtnType.BlueText}
                    layout={BtnLayout.Icon}
                    icon={faSearch}
                    link={url(strollersSearchUrl, { pageMode: PageMode.SHOW_SEARCH_FORM }, query)}
                />
            </>
        );
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        return strollersUrl;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Strollers Index - Halfpage Sticky (Desktop)",
        };
    }

    public hasMobileLogo(props: Props): boolean {
        return true;
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.strollers.brandsStrollersList", strollersReducer);
        store.injectSaga("containers.strollers.brandsStrollersList", strollersSaga);

        store.injectReducer("containers.strollers.strollersComparison", strollersComparisonReducer);
        store.injectSaga("containers.strollers.strollersComparison", strollersComparisonSaga);
    }

    public getStructuredData(props: Props): object[] {
        return [breadcrumbsBrand(props.baseUrl, props.brand)];
    }
}
